import { type RovoConnectorState } from '../useRovoConfigStore';
import { getIconUrlForSource } from './getIconUrlForSource';

/**
 * Source slug for the always-present Atlassian help/support docs aggregator
 * connector. The knowledge endpoint returns this connector unconditionally,
 * so we reproduce it here (see {@link buildHelpAggregatorConnector}).
 */
export const HELP_AGGREGATOR_SOURCE = 'help_aggregator';

/**
 * Display name the knowledge endpoint (FG-off path) returns for the
 * `help_aggregator` connector. Reproduced here so the synthetic connector
 * classifies identically downstream — in particular `is3pApp()` keys off
 * `friendlyName`, and this value is listed there as a first-party product.
 */
export const HELP_AGGREGATOR_FRIENDLY_NAME = 'Atlassian Support Docs';

/**
 * @internal Only for use by `useRovoConfigStore`.
 *
 * Builds the always-present synthetic `help_aggregator` connector that the
 * knowledge endpoint returns unconditionally. `useRovoConfigStore` adds it to
 * the connectors map once the underlying config has loaded (never in the
 * base/idle state, and never while a fetch is still in flight).
 *
 * The shape is reproduced faithfully from the knowledge endpoint's
 * (`rovo_cas_use_rovo_config=false`) response so the connector is classified
 * and displayed identically on both FG branches: it is a first-party,
 * already-authed connector. Downstream chat FE filters key off `friendlyName`
 * (via `is3pApp`) and `isUserOAuthed`, so leaving these empty/false caused the
 * connector to be filtered out of (or mis-classified in) the chat connector
 * list on the rovo-config path. See RAOC-23.
 */
export const buildHelpAggregatorConnector = (): RovoConnectorState => ({
	source: HELP_AGGREGATOR_SOURCE,
	iconUrl: getIconUrlForSource(HELP_AGGREGATOR_SOURCE),
	friendlyName: HELP_AGGREGATOR_FRIENDLY_NAME,
	// The knowledge endpoint returns this connector as already user-OAuthed; it
	// is an always-present first-party source that does not require a connect
	// flow.
	isUserOAuthed: true,
	outboundAuthUrl: '',
	connectorCreatedAt: null,
	connectedAt: null,
	resourceType: null,
	providerId: null,
	datasourceId: null,
	connectorSyncType: null,
});
